import { useFpB2bCartCalculate } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-calculate'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { useFpB2bProductsInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-products-info'
import { type GiveawayValue } from '@cart-services/types'
import { CART_FORM_ERROR_MESSAGE, isAllGiveawayValuesUnavailable, isGiveawayValuePartiallyUnavailable, type CartFormValues } from '@cart-services/zod-schema/cartFormSchema'
import { ChipFreeShipping } from '@cart-ui/ChipFreeShipping/ChipFreeShipping'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { CartFilled } from '@pets-ui/icons'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom-v5'
import { useStmCart } from '../CartProvider'
import { DIALOG_CART_THRESHOLD } from './CartFooter/DialogCartThreshold'
import { DIALOG_GIFT_NOT_ENOUGH } from './CartFooter/DialogGiftNotEnough'

export const CartFooterAction = () => {
  const { basePath, campaignId, projectCode, countryCode, campaignCode, platform, isCartInfoValidating, setIsCartInfoValidating } = useStmCart()
  const history = useHistory()
  const { openDialog: openDialogGiftNotEnough } = useDialog(DIALOG_GIFT_NOT_ENOUGH)
  const { openDialog: openDialogCartThreshold } = useDialog(DIALOG_CART_THRESHOLD)
  const { upsertCartItem } = useFpB2bCartService(campaignId, platform)
  const {
    selectedGiveaways,
    selectedRegularGiveaways,
    removeSelectedRegularGiveaways,
    removeSelectedGiveaways,
    updateSelectedGiveaways,
    selectedCouponGiveawayMap,
  } = useFpB2bCouponService(campaignId, projectCode, countryCode, campaignCode, platform)
  const { calculateResult: campaignCouponCalculateResult, isStale } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const { revalidateProductsInfo } = useFpB2bProductsInfo({ campaignId, projectCode, countryCode, campaignCode, platform })
  const noticeInfo = campaignCouponCalculateResult?.notice_info
  const shouldShowWarning = noticeInfo && noticeInfo.length > 0

  const { handleSubmit, formState: { errors }, getValues } = useFormContext<CartFormValues>()

  const checkoutUrl = `${basePath}/checkout`

  const checkout = useCallbackEvent(() => {
    history.push(checkoutUrl)
  })

  const adjustGiveawayQuantity = useCallbackEvent((giveaways: GiveawayValue[]) => {
    giveaways.forEach((giveaway) => {
      const couponCriterionId = giveaway.couponCriterionId
      if (!couponCriterionId || !selectedCouponGiveawayMap[couponCriterionId]) return
      const selectedCouponGiveawayProductMap = selectedCouponGiveawayMap[couponCriterionId]

      giveaway.giveawayValuesWithValidateStock.forEach((giveawayValue) => {
        if (isGiveawayValuePartiallyUnavailable(giveawayValue) && giveawayValue.stock !== undefined) {
          updateSelectedGiveaways(couponCriterionId, {
            ...selectedCouponGiveawayProductMap,
            [giveawayValue.product_id]: {
              quantity: giveawayValue.stock,
              project_code: selectedCouponGiveawayProductMap[giveawayValue.product_id]?.project_code ?? projectCode,
              product_id: giveawayValue.product_id,
              sku: giveawayValue.sku,
            },
          })
        }
      })
    })
  })

  useEffect(() => {
    const cartItemsError = errors?.cartItems
    const regularGiveawaysError = errors?.regularGiveaways
    const giveawaysError = errors?.giveaways
    const cartItems = getValues('cartItems')
    const regularGiveaways = getValues('regularGiveaways') || []
    const giveaways = getValues('giveaways') || []
    /**
     * 情境：
     * 如果加進購物車的商品結帳前發現庫存不足，自動調整為上限
     */
    if (cartItemsError?.some?.(error => error?.message === CART_FORM_ERROR_MESSAGE.ADJUST_TO_MAX_QUANTITY)) {
      cartItems.forEach((cartItem) => {
        if (cartItem.stock > 0 && cartItem.stock < cartItem.quantity) {
          upsertCartItem({
            cartItemId: cartItem.cartItemId,
            quantity: cartItem.stock,
          })
        }
      })
    }
    /**
     * 情境：
     * 如果所選的常態滿額禮結帳前發現庫存不足，但還有其他可選的則跳彈窗提醒使用者
     */
    if (regularGiveawaysError?.some?.(error => error?.message === CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_PARTIALLY)) {
      openDialogGiftNotEnough({
        onSubmit: checkout,
      })
    }
    /**
     * 情境：
     * 如果所選的常態滿額禮結帳前發現庫存不足，且沒有其他可選的則跳彈窗提醒使用者並且移除該常態滿額禮
     */
    if (regularGiveawaysError?.some?.(error => error?.message === CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY)) {
      openDialogGiftNotEnough({
        onSubmit: checkout,
      })
      const unavailableRegularGiveawayCriterionIds = regularGiveaways?.filter(giveaway => giveaway.isAllUnavailable)?.map(giveaway => giveaway.couponCriterionId).filter(id => id !== undefined)
      removeSelectedRegularGiveaways(unavailableRegularGiveawayCriterionIds)
    }

    /**
     * 情境：
     * 如果所選的贈品結帳前發現庫存不足，但還有其他可選的則跳彈窗提醒使用者
     * 並且自動調整為上限
     */
    if (giveawaysError?.some?.(error => error?.message === CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY_PARTIALLY)) {
      openDialogGiftNotEnough({
        onSubmit: checkout,
      })
      adjustGiveawayQuantity(giveaways)
    }

    /**
     * 情境：
     * 如果所選的贈品結帳前發現庫存不足，且沒有其他可選的則跳彈窗提醒使用者並且移除該贈品
     */
    if (giveawaysError?.some?.(error => error?.message === CART_FORM_ERROR_MESSAGE.NOT_ENOUGH_GIVEAWAY)) {
      openDialogGiftNotEnough({
        onSubmit: checkout,
      })
      const unavailableGiveawayCriterionIds = giveaways?.filter(giveaway => isAllGiveawayValuesUnavailable(giveaway.giveawayValuesWithValidateStock))?.map(giveaway => giveaway.couponCriterionId).filter(id => id !== undefined)
      removeSelectedGiveaways(unavailableGiveawayCriterionIds)
    }
  }, [adjustGiveawayQuantity, checkout, errors?.cartItems, errors?.giveaways, errors?.regularGiveaways, getValues, openDialogGiftNotEnough, removeSelectedGiveaways, removeSelectedRegularGiveaways, upsertCartItem])

  const handleCheckout = useCallbackEvent(async () => {
    // 擋購物車的通知訊息
    if (shouldShowWarning) {
      openDialogCartThreshold()
      return
    }

    try {
      // TODO: GA begin_checkout event
      setIsCartInfoValidating(true)
      const giveawayIds = selectedGiveaways.flatMap(giveaway => giveaway.products.map(product => product.product_id))
      const regularGiveawayIds = selectedRegularGiveaways.flatMap(giveaway => giveaway.products.map(product => product.product_id))
      await revalidateProductsInfo([...giveawayIds, ...regularGiveawayIds])
      await handleSubmit(checkout, (errors) => {
        console.error('[CartFooterAction] handleSubmit validation failed', JSON.stringify(errors, null, 2))
      })()
    }
    catch (error) {
      console.error(`[CartFooterAction] handleCheckout unexpect error: ${error}`)
    }
    finally {
      setIsCartInfoValidating(false)
    }
  })

  return (
    <Button loading={isCartInfoValidating || isStale} disabled={isCartInfoValidating || isStale} startIcon={<CartFilled />} type="submit" onClick={handleCheckout}>
      前往結帳
      <ChipFreeShipping shippingFee={campaignCouponCalculateResult?.shipping_total || 0} />
    </Button>
  )
}
